import { LoadingState, Button } from './ui/Primitives';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { logEvent } from '../utils/logger';
import './PhoneSection.css';

const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

export default function BillingSettings() {
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cap, setCap] = useState('');
  const [editingCap, setEditingCap] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planMessage, setPlanMessage] = useState('');
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const usage = subscription?.usage;
  const isComped = subscription?.billing_access === 'comped';
  const isMonthly = subscription?.billing_catalog_version === '2026-09';
  const canManage = subscription?.can_manage_billing === true;
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` } });

  async function refresh() {
    const token = localStorage.getItem('jwt_authorization');
    if (!token) return;
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/subscription`, { jwt_token: token });
    setSubscription(response.data);
  }
  useEffect(() => {
    const load = () => refresh().catch(() => setError('Unable to load billing status. Please refresh the page.'));
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => { if (subscription?.plan_id) setSelectedPlan(subscription.plan_id); }, [subscription?.plan_id]);
  useEffect(() => {
    if (!showPlanDialog) return undefined;
    const closeOnEscape = event => { if (event.key === 'Escape' && !busy) setShowPlanDialog(false); };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [busy, showPlanDialog]);

  async function action(callback) {
    setBusy(true); setError('');
    try { await callback(); }
    catch (err) { setError(err.response?.data?.error?.message || err.response?.data?.message || 'Unable to update billing. Please try again.'); }
    finally { setBusy(false); }
  }
  function manage() {
    logEvent({ event: { label: 'Click', data: { value: 'Manage Subscription' } } });
    action(async () => {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/stripe_portal`, { jwt_token: localStorage.getItem('jwt_authorization') });
      window.location.href = response.data.url;
    });
  }
  function saveCap(event) {
    event.preventDefault();
    const amount = cap.trim() === '' ? null : Number(cap);
    if (amount !== null && (!Number.isFinite(amount) || amount < 0 || amount > 10000)) {
      setError('Enter a limit from $0 to $10,000, or leave it blank for unlimited usage.'); return;
    }
    action(async () => {
      await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/v2/billing/spending-limit`, { overage_limit_cents: amount === null ? null : Math.round(amount * 100) }, auth());
      setEditingCap(false); await refresh();
    });
  }
  function changePlan(event) {
    event.preventDefault();
    const target = subscription.available_plans?.find(plan => plan.id === selectedPlan);
    if (!target || selectedPlan === subscription.plan_id) return;
    action(async () => {
      const requestId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/v2/billing/plan`, { plan: selectedPlan, request_id: requestId }, auth());
      await refresh();
      setPlanMessage(`Your plan is now ${target.name}.`);
      setShowPlanDialog(false);
    });
  }


  return <section className="account-security-card account-billing-card" aria-labelledby="account-billing-title">
    <h2 id="account-billing-title">Billing &amp; subscription</h2>
    {!subscription && !error && <LoadingState label="Loading billing settings…" />}
    {subscription && !canManage && <p>An organization administrator can manage your billing settings.</p>}
    {canManage && isComped && <p>Your account is comped, with all usage included.</p>}
    {canManage && !isComped && <>
      <p className="account-email">Current plan: {subscription.name}</p>
      {usage && isMonthly && <>
        <div className="account-method-row">
          <div><h3>Spending limit</h3><p>Set a monthly limit on additional AI usage charges beyond your included minutes.</p></div>
          <div className="account-billing-control">{editingCap ? <form onSubmit={saveCap}><label htmlFor="billing-overage-cap">Monthly overage limit (USD)</label><input id="billing-overage-cap" type="number" min="0" max="10000" step="0.01" value={cap} onChange={event => setCap(event.target.value)} placeholder="Unlimited" /><small>Blank allows unlimited overage. $0 stops at included minutes. Limits apply to newly started calls and allow one active AI call at a time. A call ends when its reserved minutes run out.</small><Button size="sm" disabled={busy} type="submit">Save limit</Button><Button size="sm" variant="secondary" type="button" onClick={() => setEditingCap(false)}>Cancel</Button></form> : <Button size="sm" variant="secondary" onClick={() => { setCap(usage.overage_limit_cents === null ? '' : String(usage.overage_limit_cents / 100)); setEditingCap(true); }}>Set spending limit</Button>}</div>
        </div>
        {['active', 'trialing'].includes(subscription.status_code) && subscription.available_plans?.length > 1 && <div className="account-method-row">
          <div><h3>Plan</h3><p>Choose the monthly plan and included AI minutes that fit your business.</p></div>
          <Button size="sm" variant="secondary" onClick={() => { setSelectedPlan(subscription.plan_id); setPlanMessage(''); setError(''); setShowPlanDialog(true); }}>Change plan</Button>
        </div>}
      </>}
      <div className="account-method-row">
        <div><h3>Subscription</h3><p>Update your payment method, view invoices, or cancel your subscription securely through Stripe.</p></div>
        <Button variant="secondary" size="sm" disabled={busy} onClick={manage}>Manage subscription</Button>
      </div>
      {planMessage && <p role="status">{planMessage}</p>}
    </>}
    {error && !showPlanDialog && <p className="account-error" role="alert">{error}</p>}
      {showPlanDialog && <div className="phone-plan-modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget && !busy) setShowPlanDialog(false); }}>
        <section className="phone-plan-modal" role="dialog" aria-modal="true" aria-labelledby="phone-plan-modal-title" aria-describedby="phone-plan-modal-description">
          <div className="phone-plan-modal-heading"><div><span className="say-eyebrow">Subscription</span><h2 id="phone-plan-modal-title">Change your plan</h2></div><button type="button" aria-label="Close plan dialog" disabled={busy} onClick={() => setShowPlanDialog(false)}>×</button></div>
          <p id="phone-plan-modal-description">Choose the monthly plan for this AI receptionist configuration.</p>
          <form onSubmit={changePlan}>
            <div className="phone-plan-options">{subscription.available_plans.map(plan => <label className={`phone-plan-option${selectedPlan === plan.id ? ' selected' : ''}`} key={plan.id}><input type="radio" name="billing-plan" value={plan.id} checked={selectedPlan === plan.id} onChange={event => { setSelectedPlan(event.target.value); setPlanMessage(''); setError(''); }} /><span><strong>{plan.name}</strong><small>{plan.included_minutes} AI minutes · {money(plan.overage_cents_per_minute)}/additional minute</small></span><b>{money(plan.monthly_cents)}<small>USD / month</small></b>{plan.id === subscription.plan_id && <em>Current plan</em>}</label>)}</div>
            <div className="phone-plan-notice"><strong>{subscription.status_code === 'trialing' ? 'Your trial continues.' : 'The change takes effect now.'}</strong><p>{subscription.status_code === 'trialing' ? 'Your current trial end date stays the same. The selected monthly price applies if your subscription continues after the trial.' : 'Stripe closes the current billing period, applies any credit and usage charges, and starts a new monthly period on the selected plan.'}</p></div>
            {error && <p className="phone-plan-error" role="alert">{error}</p>}
            <div className="phone-plan-modal-actions"><Button type="button" variant="secondary" disabled={busy} onClick={() => setShowPlanDialog(false)}>Cancel</Button><Button type="submit" disabled={busy || selectedPlan === subscription.plan_id} loading={busy} loadingLabel={"Changing plan…"}>{'Confirm plan change'}</Button></div>
          </form>
        </section>
      </div>}
  </section>;
}
